import { useEffect } from 'react';
import { Button, Col, Form, Row, Table } from 'react-bootstrap';
import { Link, useHistory, useLocation } from 'react-router-dom';
import edoClaimsTrackingSignal from 'signals/EdoClaimsTracking.signal';
import PaginationItems from 'components/global/Pagination';
import currencyOptions from 'components/global/Constant/currencyOptions';
import {
  applyClaimsFilters,
  clearClaimsFilters,
  fetchAndSetClaims,
  formatClaimDate,
  handleClaimsFilterChange,
  handleDownloadClaimsData,
  resolveClaimBorrowerName,
  resolveClaimFundingType,
  resolveClaimLender,
} from './EdoClaimsTracking.helpers';

const CLAIMS_PAGE_PARAM = 'claimsPage';

const EdoClaimsTracking = ({ fundingPrograms = [] }) => {
  const history = useHistory();
  const location = useLocation();
  const searchParams = Object.fromEntries(new URLSearchParams(location.search));
  const currentPage = searchParams[CLAIMS_PAGE_PARAM]
    ? Number(searchParams[CLAIMS_PAGE_PARAM])
    : 1;

  const {
    claims,
    dropdownFilters,
    totalPages,
    totalCount,
  } = edoClaimsTrackingSignal.value;

  useEffect(() => {
    fetchAndSetClaims(currentPage);
  }, [currentPage, location.search]);

  const resetToClaimsPageOne = () => {
    const next = new URLSearchParams(location.search);
    next.set('trackingTab', 'claims');
    next.delete(CLAIMS_PAGE_PARAM);
    history.push({
      pathname: location.pathname,
      search: `?${next.toString()}`,
    });
  };

  return (
    <div className="bg-white p-32 border border-gray shadow-sm">
      <div className="d-flex flex-row justify-content-between mb-16 align-items-start">
        <Form
          className="flex-grow-1"
          onSubmit={(e) => {
            e.preventDefault();
            resetToClaimsPageOne();
            applyClaimsFilters(1);
          }}
        >
          <Row className="align-items-end">
            <Col md={3}>
              <Form.Group>
                <Form.Label>Program</Form.Label>
                <Form.Control
                  as="select"
                  value={dropdownFilters.fundingProgramId || ''}
                  onChange={(e) =>
                    handleClaimsFilterChange('fundingProgramId', e.target.value)
                  }
                >
                  <option value="">All</option>
                  {(fundingPrograms || []).map((program) => (
                    <option key={program.id} value={program.id}>
                      {program.name || program.key || program.id}
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Claim Submitted Date From</Form.Label>
                <Form.Control
                  type="date"
                  value={dropdownFilters.claimSubmittedDateFrom || ''}
                  onChange={(e) =>
                    handleClaimsFilterChange(
                      'claimSubmittedDateFrom',
                      e.target.value
                    )
                  }
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Claim Submitted Date To</Form.Label>
                <Form.Control
                  type="date"
                  value={dropdownFilters.claimSubmittedDateTo || ''}
                  onChange={(e) =>
                    handleClaimsFilterChange(
                      'claimSubmittedDateTo',
                      e.target.value
                    )
                  }
                />
              </Form.Group>
            </Col>
            <Col md={3} className="d-flex mb-3">
              <Button type="submit" className="rounded mr-8">
                Apply
              </Button>
              <Button
                type="button"
                variant="outline-secondary"
                className="rounded"
                onClick={() => {
                  resetToClaimsPageOne();
                  clearClaimsFilters(1);
                }}
              >
                Clear
              </Button>
            </Col>
          </Row>
        </Form>
        <Button
          className="rounded ml-16"
          variant="outline-info"
          onClick={handleDownloadClaimsData}
        >
          Download
        </Button>
      </div>

      <p className="text-muted mb-16">
        {totalCount} claim{totalCount === 1 ? '' : 's'} found
      </p>

      <Table responsive striped bordered hover>
        <thead>
          <tr>
            <th>CalCAP Loan #</th>
            <th>Lender Name</th>
            <th>Borrower Name</th>
            <th>Date Claim Amount Paid</th>
            <th>Lender Loan #</th>
            <th>Claim Submitted Date</th>
            <th>Loan Charge Off Date</th>
            <th>Amount Filed</th>
            <th>Funding Type</th>
            <th>Claim Amount Approved</th>
          </tr>
        </thead>
        <tbody>
          {claims.length === 0 && (
            <tr>
              <td colSpan={10} className="text-center text-muted">
                No claims found. Adjust filters and click Apply.
              </td>
            </tr>
          )}
          {claims.map((claim) => {
            const app = claim.application || {};
            const lender = resolveClaimLender(claim);
            const appId = app.id;
            return (
              <tr key={claim.id}>
                <td>
                  {appId ? (
                    <Link to={`/edo/portfolio/${appId}?tab=claims`}>
                      {app.calcapLoanNumber || appId}
                    </Link>
                  ) : (
                    app.calcapLoanNumber || ''
                  )}
                </td>
                <td>{lender?.name || ''}</td>
                <td>{resolveClaimBorrowerName(claim)}</td>
                <td>{formatClaimDate(claim.amountPaidDate)}</td>
                <td>{app.lenderLoanNumber || ''}</td>
                <td>{formatClaimDate(claim.claimSubmittedDate)}</td>
                <td>{formatClaimDate(claim.chargeOffDate)}</td>
                <td>
                  {claim.filedAmount != null
                    ? Number(claim.filedAmount).toLocaleString(
                        'en-US',
                        currencyOptions.currencyUS
                      )
                    : ''}
                </td>
                <td>{resolveClaimFundingType(claim)}</td>
                <td>
                  {claim.approvedClaimAmount != null
                    ? Number(claim.approvedClaimAmount).toLocaleString(
                        'en-US',
                        currencyOptions.currencyUS
                      )
                    : ''}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>

      {totalPages > 1 && (
        <PaginationItems
          className="justify-content-center mt-24"
          totalPages={totalPages}
          pageParam={CLAIMS_PAGE_PARAM}
        />
      )}
    </div>
  );
};

export default EdoClaimsTracking;
